import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbsenceRequestDirective } from '@app/+modals/employee/absence-request/absence-request.directive';
import { getEnhancedAbsenceById } from '@app/reducers/orm/absence/absence.service';
import { AbsenceTooltipComponent } from '@app/shared/absence/absence-tooltip/absence-tooltip.component';
import { Store } from '@ngrx/store';
import { AppState } from '@reducers/interfaces';
import { AbsenceModel, EnhancedAbsenceModel } from '@reducers/orm/absence/absence.model';
import { scheduleCompactViewEnabled } from '@reducers/page-filters/page-filters.selector';
import { TooltipDirective } from '@sb/tooltip';
import { take } from 'rxjs/operators';

import { AbsenceItemContentComponent } from './absence-item-content/absence-item-content.component';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'absence-item',
  templateUrl: './absence-item.component.html',
  imports: [TooltipDirective, AbsenceRequestDirective, AbsenceTooltipComponent, AbsenceItemContentComponent],
})
export class AbsenceItemComponent implements OnInit, OnDestroy {
  private store = inject<Store<AppState>>(Store);
  private destroyRef = inject(DestroyRef);

  @Input({ required: true })
  set absence(absence: AbsenceModel | EnhancedAbsenceModel) {
    if (absence.absenteeOption) {
      this.enhancedAbsence = absence as EnhancedAbsenceModel;
    } else {
      this.setEnhancedAbsence(absence.id);
    }
  }

  @Input({ required: true })
  date: string;

  @ViewChild(TooltipDirective, { static: true })
  tooltip: TooltipDirective;

  @Input()
  mode: 'team' | 'employee' = 'employee';

  @Input()
  compactView: boolean;

  // This prop is used in the template to make sure absence-item-content
  // doesn't have to "enhance" the absence again
  enhancedAbsence: EnhancedAbsenceModel;

  ngOnInit(): void {
    if (this.compactView === undefined) {
      void this.store
        .select(scheduleCompactViewEnabled)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((compactViewEnabled: boolean) => {
          this.compactView = compactViewEnabled;
        });
    }
  }

  ngOnDestroy(): void {
    if (this.tooltip) {
      this.tooltip.hide();
    }
  }

  private setEnhancedAbsence(id: string) {
    void this.store
      .select(getEnhancedAbsenceById(id))
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((absence: EnhancedAbsenceModel) => {
        this.enhancedAbsence = absence;
      });
  }
}
